import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import { toast } from "sonner";
import axios from "axios";
import {
  ArrowLeft,
  Download,
  Loader2,
  CheckCircle2,
  XCircle,
  Clock,
  ArrowRight,
  Music2,
  RefreshCw,
  Calendar,
  Database,
  Zap,
  AlertTriangle,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusColors = {
  completed: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
  failed: "bg-red-500/10 text-red-400 border-red-500/20",
  searching: "bg-amber-500/10 text-amber-400 border-amber-500/20",
  queued: "bg-blue-500/10 text-blue-400 border-blue-500/20",
  partial: "bg-purple-500/10 text-purple-400 border-purple-500/20",
  running: "bg-amber-500/10 text-amber-400 border-amber-500/20",
};

const BatchDetail = () => {
  const { id } = useParams();
  const [batch, setBatch] = useState(null);
  const [searches, setSearches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [polling, setPolling] = useState(false);
  const [fetchingAll, setFetchingAll] = useState(false);
  const [fetchStatus, setFetchStatus] = useState(null);
  const [fetchPolling, setFetchPolling] = useState(false);

  const fetchBatch = useCallback(async () => {
    try {
      const [batchRes, searchesRes] = await Promise.all([
        axios.get(`${API}/batch/${id}`),
        axios.get(`${API}/batch/${id}/searches`),
      ]);
      
      setBatch(batchRes.data);
      setSearches(searchesRes.data.searches);
      
      if (batchRes.data.status === "running" || batchRes.data.status === "queued") {
        setPolling(true);
      } else {
        setPolling(false);
      }
      
      // Check if fetch is in progress
      if (batchRes.data.fetch_status === "processing") {
        setFetchPolling(true);
      }
    } catch (error) {
      console.error("Failed to fetch batch:", error);
      toast.error("Failed to load batch");
    } finally {
      setLoading(false);
    }
  }, [id]);

  const fetchFetchStatus = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/batch/${id}/fetch-status`);
      setFetchStatus(response.data);
      
      if (response.data.fetch_status === "completed" || response.data.fetch_status === "idle") {
        setFetchPolling(false);
        if (response.data.fetch_status === "completed") {
          toast.success(`Data fetched for ${response.data.data_fetched} tracks`);
          fetchBatch(); // Refresh the searches list
        }
      }
    } catch (error) {
      console.error("Failed to fetch status:", error);
    }
  }, [id, fetchBatch]);

  useEffect(() => {
    fetchBatch();
  }, [fetchBatch]);

  useEffect(() => {
    if (polling) {
      const interval = setInterval(fetchBatch, 3000);
      return () => clearInterval(interval);
    }
  }, [polling, fetchBatch]);

  useEffect(() => {
    if (fetchPolling) {
      const interval = setInterval(fetchFetchStatus, 2000);
      return () => clearInterval(interval);
    }
  }, [fetchPolling, fetchFetchStatus]);

  const handleFetchAllData = async () => {
    const completedCount = searches.filter(s => s.status === "completed").length;
    
    if (completedCount === 0) {
      toast.error("No completed tracks to fetch data for");
      return;
    }
    
    if (!window.confirm(`This will fetch credits and PRO data for ${completedCount} tracks. This may take a few minutes. Continue?`)) {
      return;
    }
    
    setFetchingAll(true);
    try {
      const response = await axios.post(`${API}/batch/${id}/fetch-all-data`);
      toast.success(response.data.message);
      setFetchPolling(true);
      setFetchStatus({ fetch_status: "processing", fetch_progress: 0 });
    } catch (error) {
      console.error("Failed to start fetch:", error);
      toast.error("Failed to start data fetch");
    } finally {
      setFetchingAll(false);
    }
  };

  const handleExportCSV = async () => {
    try {
      const response = await axios.get(`${API}/export/batch/${id}.csv`, {
        responseType: 'blob'
      });
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `batch_${id}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('CSV exported successfully');
    } catch (error) {
      toast.error('Failed to export CSV');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-syncchain-accent" />
      </div>
    );
  }

  if (!batch) {
    return (
      <div className="text-center py-12">
        <p className="text-syncchain-text-secondary">Batch not found</p>
        <Button asChild variant="link" className="text-syncchain-accent mt-2">
          <Link to="/batches">Return to Batch CSV</Link>
        </Button>
      </div>
    );
  }

  const progress = batch.total_rows > 0
    ? Math.round(((batch.completed_rows + batch.failed_rows) / batch.total_rows) * 100)
    : 0;

  return (
    <div className="space-y-6" data-testid="batch-detail-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
        <div className="flex items-start gap-4">
          <Button
            asChild
            variant="ghost"
            size="icon"
            className="text-syncchain-text-secondary hover:text-syncchain-text shrink-0"
          >
            <Link to="/batches">
              <ArrowLeft className="w-5 h-5" />
            </Link>
          </Button>
          <div>
            <div className="flex items-center gap-3 mb-2">
              <h1 className="font-heading text-2xl font-bold text-syncchain-text">
                {batch.name}
              </h1>
              <Badge
                className={`${statusColors[batch.status]} border-0 text-xs font-mono uppercase`}
              >
                {batch.status === "running" && <Loader2 className="w-3 h-3 mr-1 animate-spin" />}
                {batch.status}
              </Badge>
            </div>
            <div className="flex items-center gap-4 text-sm text-syncchain-text-secondary">
              <span className="flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                {formatDate(batch.created_at)}
              </span>
              <span>{batch.total_rows} tracks</span>
            </div>
            {batch.notes && (
              <p className="text-sm text-syncchain-text-muted mt-2">{batch.notes}</p>
            )}
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={handleFetchAllData}
            variant="outline"
            size="sm"
            disabled={fetchingAll || fetchPolling || batch.status === "running" || batch.status === "queued"}
            className="border-syncchain-accent/30 text-syncchain-accent hover:bg-syncchain-accent/10"
            data-testid="fetch-all-data-btn"
          >
            {fetchingAll || fetchPolling ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Zap className="w-4 h-4 mr-2" />
            )}
            {fetchPolling ? "Fetching..." : "Fetch All Data"}
          </Button>
          <Button
            onClick={handleExportCSV}
            variant="outline"
            size="sm"
            className="border-white/10 text-syncchain-text-secondary hover:text-syncchain-text hover:bg-white/5"
            data-testid="export-batch-csv-btn"
          >
            <Download className="w-4 h-4 mr-2" />
            Export CSV
          </Button>
          {polling && (
            <Button
              onClick={fetchBatch}
              variant="outline"
              size="sm"
              className="border-white/10 text-syncchain-accent"
            >
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              Refreshing...
            </Button>
          )}
        </div>
      </div>

      {/* Progress Card */}
      <Card className="bg-syncchain-paper border-white/10">
        <CardContent className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-xs font-mono uppercase tracking-wider text-syncchain-text-muted mb-1">
                Processing Progress
              </p>
              <p className="text-3xl font-heading font-bold text-syncchain-text">
                {progress}%
              </p>
            </div>
            <div className="flex gap-6 text-sm">
              <div className="text-center">
                <p className="text-2xl font-heading font-bold text-syncchain-success">
                  {batch.completed_rows}
                </p>
                <p className="text-xs text-syncchain-text-muted">Completed</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-heading font-bold text-syncchain-error">
                  {batch.failed_rows}
                </p>
                <p className="text-xs text-syncchain-text-muted">Failed</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-heading font-bold text-syncchain-info">
                  {batch.total_rows - batch.completed_rows - batch.failed_rows}
                </p>
                <p className="text-xs text-syncchain-text-muted">Pending</p>
              </div>
            </div>
          </div>
          <Progress value={progress} className="h-2 bg-syncchain-surface" />
        </CardContent>
      </Card>

      {/* Data Fetch Progress Card */}
      {(fetchPolling || fetchStatus?.fetch_status === "completed") && (
        <Card className="bg-syncchain-paper border-syncchain-accent/30">
          <CardContent className="p-6">
            <div className="flex items-center gap-3 mb-4">
              <Database className="w-5 h-5 text-syncchain-accent" />
              <div className="flex-1">
                <p className="text-xs font-mono uppercase tracking-wider text-syncchain-text-muted mb-1">
                  Data Fetch Progress
                </p>
                <p className="text-xl font-heading font-bold text-syncchain-text">
                  {fetchStatus?.fetch_status === "processing" ? (
                    <span className="flex items-center gap-2">
                      <Loader2 className="w-5 h-5 animate-spin text-syncchain-accent" />
                      Fetching credits & PRO data... {fetchStatus?.fetch_progress || 0}%
                    </span>
                  ) : fetchStatus?.fetch_status === "completed" ? (
                    <span className="flex items-center gap-2 text-syncchain-success">
                      <CheckCircle2 className="w-5 h-5" />
                      Complete - {fetchStatus?.data_fetched || 0} tracks with data
                    </span>
                  ) : (
                    "Ready to fetch"
                  )}
                </p>
              </div>
              {fetchStatus?.fetch_errors?.length > 0 && (
                <Badge variant="outline" className="bg-amber-500/10 text-amber-400 border-amber-500/20">
                  <AlertTriangle className="w-3 h-3 mr-1" />
                  {fetchStatus.fetch_errors.length} errors
                </Badge>
              )}
            </div>
            {fetchPolling && (
              <Progress value={fetchStatus?.fetch_progress || 0} className="h-2 bg-syncchain-surface" />
            )}
            {fetchStatus?.fetch_errors?.length > 0 && (
              <div className="mt-4 p-3 bg-amber-500/10 rounded-lg">
                <p className="text-xs font-mono uppercase text-amber-400 mb-2">Errors:</p>
                <div className="space-y-1 max-h-32 overflow-y-auto">
                  {fetchStatus.fetch_errors.slice(0, 5).map((err, idx) => (
                    <p key={idx} className="text-xs text-amber-300">
                      • {err.title}: {err.error}
                    </p>
                  ))}
                  {fetchStatus.fetch_errors.length > 5 && (
                    <p className="text-xs text-amber-400">
                      ...and {fetchStatus.fetch_errors.length - 5} more
                    </p>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Searches List */}
      <Card className="bg-syncchain-paper border-white/10">
        <CardHeader>
          <CardTitle className="font-heading text-lg text-syncchain-text">
            Track Results
          </CardTitle>
        </CardHeader>
        <CardContent>
          {searches.length > 0 ? (
            <div className="space-y-2">
              {searches.map((search) => (
                <Link
                  key={search.id}
                  to={`/search/${search.id}`}
                  className="flex items-center gap-4 p-4 rounded-lg bg-syncchain-surface/50 border border-white/5 hover:border-syncchain-accent/30 transition-all group"
                  data-testid={`batch-search-${search.id}`}
                >
                  <div className="w-8 h-8 rounded-lg bg-syncchain-surface flex items-center justify-center shrink-0">
                    <span className="text-xs font-mono text-syncchain-text-muted">
                      {(search.row_index + 1).toString().padStart(2, '0')}
                    </span>
                  </div>
                  <div className="w-10 h-10 rounded-lg bg-syncchain-surface flex items-center justify-center shrink-0">
                    <Music2 className="w-5 h-5 text-syncchain-text-secondary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-syncchain-text truncate">
                      {search.track_title || search.query_title || search.input_row?.title || "Untitled"}
                    </p>
                    <p className="text-sm text-syncchain-text-secondary truncate">
                      {search.track_artist || search.query_artist || search.input_row?.artist || "Unknown Artist"}
                    </p>
                  </div>
                  {search.confidence?.overall !== undefined && (
                    <div className="text-right shrink-0">
                      <p className="text-sm font-mono text-syncchain-text">
                        {Math.round(search.confidence.overall * 100)}%
                      </p>
                      <p className="text-xs text-syncchain-text-muted">confidence</p>
                    </div>
                  )}
                  <Badge
                    variant="outline"
                    className={`${statusColors[search.status]} text-xs font-mono uppercase shrink-0`}
                  >
                    {search.status === "searching" && <Loader2 className="w-3 h-3 mr-1 animate-spin" />}
                    {search.status === "completed" && <CheckCircle2 className="w-3 h-3 mr-1" />}
                    {search.status === "failed" && <XCircle className="w-3 h-3 mr-1" />}
                    {search.status === "queued" && <Clock className="w-3 h-3 mr-1" />}
                    {search.status}
                  </Badge>
                  <ArrowRight className="w-4 h-4 text-syncchain-text-muted group-hover:text-syncchain-accent transition-colors shrink-0" />
                </Link>
              ))}
            </div>
          ) : (
            <p className="text-center text-syncchain-text-muted py-8">
              No tracks in this batch
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default BatchDetail;
